import {
  pgTable,
  text,
  timestamp,
  uuid,
  jsonb,
  index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

// CQ2: Valid entity types for disputes — exported for service-layer validation
export const DISPUTE_ENTITY_TYPES = ["touchpoint", "conversion"] as const;
export type DisputeEntityType = (typeof DISPUTE_ENTITY_TYPES)[number];

export const DISPUTE_TYPES = ["incorrect_attribution", "duplicate_touchpoint", "missing_touchpoint", "incorrect_value", "other"] as const;
export type DisputeType = (typeof DISPUTE_TYPES)[number];

export const DISPUTE_STATUSES = ["open", "investigating", "resolved", "rejected"] as const;
export type DisputeStatus = (typeof DISPUTE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Attribution Disputes — APPEND-ONLY annotations on attribution data (F6)
// ---------------------------------------------------------------------------
// IMMUTABILITY: This table MUST be append-only at the service layer.
// No UPDATE, no DELETE. Attribution touchpoints and conversions are immutable
// (AP-4 append-only ledger). Disputes are the ONLY way to annotate them.
// Disputes resolve via status progression, not mutation of the underlying data.

export const attributionDisputes = pgTable("attribution_disputes", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  // CQ2: Generic entity reference — entityType discriminates, entityId is uuid FK
  entityType: text("entity_type").notNull(), // DisputeEntityType: "touchpoint" | "conversion"
  entityId: uuid("entity_id").notNull(), // FK to attribution_touchpoints.id or attribution_conversions.id
  disputeType: text("dispute_type").notNull(), // DisputeType
  status: text("status").notNull().default("open"), // DisputeStatus
  description: text("description").notNull(),
  evidence: jsonb("evidence").$type<Record<string, unknown>>(),
  resolution: text("resolution"), // Explanation of outcome (set when resolved/rejected)
  raisedBy: uuid("raised_by").notNull(), // Staff user who opened the dispute
  resolvedBy: uuid("resolved_by"), // Staff user who resolved
  resolvedAt: timestamp("resolved_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  // No updatedAt — append-only
}, (table) => [
  index("idx_attr_dispute_tenant").on(table.tenantId),
  index("idx_attr_dispute_entity").on(table.tenantId, table.entityType, table.entityId),
  index("idx_attr_dispute_status").on(table.tenantId, table.status),
]);
